import React, { useEffect } from 'react';
import { Button, Card, Col, Form, Row, Space, Switch } from 'antd';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import TableBase from 'common/components/table/table-base';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import {
  filterColumnsByPersonalization,
  getTablePersonalizationColumnsData,
  getTablePersonalizationSortData,
  personalizationDefaultTaleSort,
} from 'common/components/table/table-helpers';
import visitorBanListService from 'common/services/visitor-ban-list-service/visitor-ban-list.service';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import { observer } from 'mobx-react-lite';
import { visitorBanListInitialColumns } from './visitor-ban-list.config';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import VisitorBanEntryAddEditModal, { IVisitorBanEntryAddEditModal } from './add-edit-visitor-ban-entry-modal';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { PlusOutlined } from '@ant-design/icons';
import { IConfirmDeleteModalProps } from 'common/components/modal/confirm-delete-modal/confirm-delete-modal.types';
import ConfirmDeleteModal from 'common/components/modal/confirm-delete-modal/confirm-delete-modal';
import configurationStore from 'common/stores/configuration/configuration.store';
import { IAdvisoryConfigurationUpdate } from 'common/services/configuration-service/configuration.service.types';
import configurationService from 'common/services/configuration-service/configuration.service';
import { notification } from 'common/utils/notification';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import './visitor-ban-list.scss';

const TABLE_NAME = 'advisory';

const VisitorBanList = observer(() => {
  const [form] = Form.useForm();
  const initData = configurationStore.configuration?.advisory;
  let timeoutId: any;

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      form.submit();
    }, 1500);
  };

  const onFinish = async (values: any) => {
    const model: IAdvisoryConfigurationUpdate = {
      advisory: { isDisplayReasonForSecureOfficer: values.isDisplayReasonForSecureOfficer },
    };

    const result = await configurationService.updateSystemConfiguration(model);
    configurationStore.setConfiguration(result);
    notification.success({
      message: 'Configurations successfully updated',
      duration: 5,
    });
  };

  useEffect(() => {
    if (form) {
      form.setFieldsValue(initData);
    }
  }, [form, initData]);

  const tableControls = useTableControls({
    onFetch: visitorBanListService.getVisitorBanList,
    initialColumns: filterColumnsByPersonalization(visitorBanListInitialColumns, TABLE_NAME),
    defaultSort: personalizationDefaultTaleSort(TABLE_NAME),
    onUpdateColumns: async (columns: any, sortBy: any) => {
      await userPersonalizationStore.updateTableColumnsPersonalization({
        tableName: TABLE_NAME,
        tableColumns: getTablePersonalizationColumnsData(columns),
        tableSort: getTablePersonalizationSortData(sortBy),
      });
    },
  });

  const addModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const defaultData: IVisitorBanEntryAddEditModal = {
        type: 'Person',
        id: null,
        firstName: null,
        lastName: null,
        companyName: null,
        licensePlate: null,
        action: null,
        reason: null,
      };
      setModalInitData(defaultData);
    },
    onClose: (isSuccess) => isSuccess && tableControls.refetchData(),
  });

  const editModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const selectedRow = tableControls.getSelectedRow();
      if (!selectedRow) {
        return;
      }
      setModalInitData(selectedRow);
    },
    onClose: (isSuccess, updatedRow) => {
      if (isSuccess) {
        tableControls.updateSelectedRow(updatedRow);
      }
    },
  });

  const deleteModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const selectedRow = tableControls.getSelectedRow();
      if (!selectedRow) {
        return;
      }

      const data: IConfirmDeleteModalProps = {
        message: 'Are you sure you want to delete this record?',
        onDelete: () => {
          visitorBanListService.delete(selectedRow.id).then((result) => {
            deleteModal.hideModal(true);
          });
        },
      };

      setModalInitData(data);
    },
    onClose: (isSuccess) => {
      if (isSuccess) {
        tableControls.setSelectedRow(null);
        tableControls.refetchData();
      }
    },
  });
  return (
    <>
      <TableContextProvider controls={tableControls}>
        <Card className="eiq-card eiq-card-border full-card">
          <Row className="eiq-card-header">
            <Col span={24} style={{ marginBottom: 8 }}>
              <h1 className="h1">Advisory</h1>
            </Col>
            <FormWrapper
              form={form}
              onValuesChange={handleFormValuesChange}
              onFinish={onFinish}
              layout="vertical"
              style={{ width: '100%' }}>
              <Col xs={24} sm={12}>
                <Space className="full-width drive-id-configuration-wrapper" size={13}>
                  <div className="drive-id-configuration">
                    <div>Display the reason to the security officer in the warning message.</div>
                  </div>
                  <Form.Item name="isDisplayReasonForSecureOfficer" valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Switch />
                  </Form.Item>
                </Space>
              </Col>
            </FormWrapper>
          </Row>

          <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
            <Col flex="auto">
              <SearchFilter
                placeholder="Search by first name, last name, company name, license plate"
                rulesForColumn={{
                  firstName: FilterTypesEnum.Contains,
                  lastName: FilterTypesEnum.Contains,
                  companyName: FilterTypesEnum.Contains,
                  licensePlate: FilterTypesEnum.Contains,
                }}
              />
            </Col>
            <Col>
              <Space size={8}>
                <Button
                  icon={<DeleteIcon stroke="#505762" />}
                  size="middle"
                  disabled={!tableControls.getSelectedRow()}
                  onClick={deleteModal.showModal}>
                  Delete
                </Button>
                <Button icon={<EditIcon />} size="middle" disabled={!tableControls.getSelectedRow()} onClick={editModal.showModal}>
                  Edit
                </Button>
                <Button type="primary" icon={<PlusOutlined />} size="middle" onClick={addModal.showModal}>
                  Add
                </Button>
              </Space>
            </Col>
          </Row>
          <div className="advisory-table-wrapper">
            <TableBase onSelected={tableControls.setSelectedRow} selectFirstRowByDefault estimatedRowHeight={120} />
          </div>
        </Card>
      </TableContextProvider>
      <VisitorBanEntryAddEditModal
        title="Add"
        initData={addModal.initData}
        isEdit={false}
        isOpen={addModal.isOpenModal}
        onClose={addModal.hideModal}
      />
      <VisitorBanEntryAddEditModal
        title="Edit"
        isEdit={true}
        initData={editModal?.initData}
        isOpen={editModal.isOpenModal}
        onClose={editModal.hideModal}
      />
      <ConfirmDeleteModal
        isEdit={false}
        initData={deleteModal?.initData}
        title="Delete"
        isOpen={deleteModal?.isOpenModal}
        onClose={deleteModal?.hideModal}
      />
    </>
  );
});

export default VisitorBanList;
